import { useState } from "owa-react";
import { FluentButton } from "owa-fluent-v9-shims";
import { observer } from "owa-mobx-react";
import React from "react";
import { Module } from "owa-workloads-module";
import { UNIQUE_MESSAGE_BODY_SELECTOR } from "owa-controls-content-handler/lib/constants";

import { skipToMainContentLink } from "./SkipLinkControl.scss";
import loc from "owa-localize";
import { skipLinkControlButtonAriaLabel } from "./SkipLinkControl.locstring.json";

const skipLinkNameAttribute = "data-skip-link-name";

export interface SkipLinkControlProps {
    currentModule: Module;
    targetWindow?: Window;
}

const defaultExport: React.FunctionComponent<SkipLinkControlProps> = observer(function SkipLinkControl(props: SkipLinkControlProps) {
    const { currentModule, targetWindow } = props;
    
    // The element that the skip link will focus on
    /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
     * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
     *	> Using HTMLElement state is necessary here to trigger useMemo re-computation when skip link target changes */
    const [skipLinkTarget, setSkipLinkTarget] = useState<HTMLElement | null>(null);
    
    const onSkipLinkButtonClicked = React.useCallback(() => {
        skipLinkTarget?.focus();
    }, [skipLinkTarget]);
    
    const [buttonText, setButtonText] = useState<string>("");
    const [customAriaLabel, setCustomAriaLabel] = useState<string>("");
    
    const updateSkipLinkTarget = (updatedSkipLinkTarget: HTMLElement | null, customButtonText?: string, useDefaultAriaLabel?: boolean) => {
        // Reset alt button text when skip link target changes
        setSkipLinkTarget(updatedSkipLinkTarget);
        if (customButtonText) {
            setButtonText(customButtonText);
        }
        else if (updatedSkipLinkTarget) {
            const skipLinkName = updatedSkipLinkTarget.getAttribute(skipLinkNameAttribute);
            if (skipLinkName) {
                setButtonText(skipLinkName);
            }
        }
        else {
            setButtonText("");
        }
        
        if (updatedSkipLinkTarget && !useDefaultAriaLabel) {
            const ariaLabel = updatedSkipLinkTarget.getAttribute("aria-label");
            if (ariaLabel) {
                setCustomAriaLabel(ariaLabel);
            }
        }
        else {
            setCustomAriaLabel("");
        }
    };
    
    // Callback function for when the skip link button is focused
    //
    // Modules can register logic to select the appropriate skip link target elemen
    // (its main content available at time of focus).
    const onSkipLinkButtonFocused = React.useCallback(() => {
        const targetDocument = targetWindow ? targetWindow.document : document;
        if (currentModule === Module.Mail) {
            const skipLinkTargetElements = targetDocument.querySelectorAll(`[${skipLinkNameAttribute}]`);
            
            // If there are 2 elements with a data-skip-link-name attribute, then
            // we need to determine if we should be targeting the reading pane
            // (higher priority) or the message list (lower priority). If the RP
            // is open, then we should target it (item at index 1). Otherwise, we
            // should target the message list (item at index 0).
            if (skipLinkTargetElements?.length === 2) {
                const conversationReadingPaneElement = targetDocument.getElementById("ConversationReadingPaneContainer");
                const itemReadingPaneElement = targetDocument.getElementById("ItemReadingPaneContainer");
                // If either element exists, it means there's content in the RP
                // and it should be targeted. Otherwise, the message list should
                // be targeted.
                // If the RP is open, we should target the message body element
                // Otherwise target the reading pane container
                if (conversationReadingPaneElement || itemReadingPaneElement) {
                    const rpContainer = skipLinkTargetElements.item(1);
                    const messageBodyElement = tryGetMessageBodySkipLinkTarget(conversationReadingPaneElement, itemReadingPaneElement);
                    
                    if (messageBodyElement) {
                        updateSkipLinkTarget(messageBodyElement as HTMLElement, messageBodyElement.getAttribute(skipLinkNameAttribute) ?? "", true /* useDefaultAriaLabel */);
                    }
                    else {
                        updateSkipLinkTarget(rpContainer as HTMLElement);
                    }
                }
                else {
                    handleMessageListSkipLink(skipLinkTargetElements.item(0) as HTMLElement);
                }
            }
            else if (skipLinkTargetElements?.length === 1) {
                // If there's only one element with a data-skip-link-name attribute,
                // then it should be targeted (it's the ML in the case of RP off).
                const element = skipLinkTargetElements.item(0) as HTMLElement;
                if (element.getAttribute("data-app-section") === "NotesPane") {
                    updateSkipLinkTarget(element); // Notes pane — reads its own aria-label
                }
                else {
                    handleMessageListSkipLink(element); // real message list — existing behaviour
                }
            }
            else {
                updateSkipLinkTarget(null);
            }
            
            return;
        }
        else {
            // Always re-evaluate the first registered skip-link target on focus so
            // dynamic enable/disable of regions is reflected immediately.
            const skipLinkTargetElement = targetDocument
                .querySelectorAll(`[${skipLinkNameAttribute}]`)
                ?.item(0) as HTMLElement | null;
            
            if (skipLinkTargetElement) {
                if (skipLinkTargetElement !== skipLinkTarget) {
                    updateSkipLinkTarget(skipLinkTargetElement);
                }
            }
            else if (skipLinkTarget) {
                updateSkipLinkTarget(null);
            }
        }
    }, [skipLinkTarget, currentModule, targetWindow]);
    
    // Try to get the message body element as the skip link target, otherwise use the reading pane container
    const tryGetMessageBodySkipLinkTarget = (conversationReadingPaneElement: HTMLElement | null, itemReadingPaneElement: HTMLElement | null) => {
        let messageBodySkipLinkTarget;
        if (conversationReadingPaneElement) {
            messageBodySkipLinkTarget = conversationReadingPaneElement.querySelector("#focused " + UNIQUE_MESSAGE_BODY_SELECTOR);
        }
        else if (itemReadingPaneElement) {
            messageBodySkipLinkTarget = itemReadingPaneElement.querySelector(UNIQUE_MESSAGE_BODY_SELECTOR);
        }
        
        return messageBodySkipLinkTarget;
    };
    
    const handleMessageListSkipLink = (targetElement: HTMLElement) => {
        const ml = targetElement;
        
        // If there is a focusable row in the message list, we should target it.
        const targetRow = ml
            .querySelectorAll("[data-focusable-row=true]")
            .item(0) as HTMLElement;
        
        // If there is a focusable row, we should set the button text to the
        // value of the data-skip-link-name attribute on the parent container (ML).
        if (targetRow) {
            updateSkipLinkTarget(targetRow, ml.getAttribute(skipLinkNameAttribute) ?? "", true /* useDefaultAriaLabel */);
        }
        else {
            // If there is no focusable row, we should target the message list itself.
            updateSkipLinkTarget(ml);
        }
    };
    
    const skipLink = React.useMemo(() => {
        return (<FluentButton className={skipToMainContentLink} onClick={onSkipLinkButtonClicked} onFocus={onSkipLinkButtonFocused} role="link" aria-label={customAriaLabel || loc(skipLinkControlButtonAriaLabel)}>
                    {buttonText}
                </FluentButton>);
    }, [
        onSkipLinkButtonClicked,
        onSkipLinkButtonFocused,
        skipLinkTarget,
        buttonText,
        customAriaLabel,
    ]);
    
    return skipLink;
}, "SkipLinkControl");
export default defaultExport;
